import { QueryClient } from '@tanstack/react-query';
import { FallbackDataService } from './fallbackDataService';
import { performanceMonitor } from './performanceMonitor';

export interface CachePruningConfig {
  maxCacheSize: number; // Maximum number of entries
  maxCacheAge: number; // Maximum age in milliseconds
  pruningInterval: number; // How often to run pruning
  keepTopN: number; // Keep top N most accessed items
  criticalQueryKeys: string[]; // Never prune these
}

export interface PruningResult {
  entriesRemoved: number;
  spaceFreed: number;
  criticalEntriesPreserved: number;
  pruningDuration: number;
}

export class CachePruningService {
  private queryClient: QueryClient;
  private fallbackService: FallbackDataService;
  private config: CachePruningConfig;
  private pruningTimer: NodeJS.Timeout | null = null;
  private lastPruningTime = 0;

  constructor(
    queryClient: QueryClient,
    config: Partial<CachePruningConfig> = {}
  ) {
    this.queryClient = queryClient;
    this.fallbackService = new FallbackDataService(queryClient);
    this.config = {
      maxCacheSize: 500, // 500 entries
      maxCacheAge: 24 * 60 * 60 * 1000, // 24 hours
      pruningInterval: 30 * 60 * 1000, // 30 minutes
      keepTopN: 100, // Keep top 100 most accessed
      criticalQueryKeys: ['tracking', 'campaigns'],
      ...config,
    };
  }

  public startAutoPruning(): void {
    if (this.pruningTimer) {
      return; // Already running
    }

    this.pruningTimer = setInterval(() => {
      this.performPruning();
    }, this.config.pruningInterval);

    console.info('Automatic cache pruning started');
  }

  public stopAutoPruning(): void {
    if (this.pruningTimer) {
      clearInterval(this.pruningTimer);
      this.pruningTimer = null;
      console.info('Automatic cache pruning stopped');
    }
  }

  public async performPruning(): Promise<PruningResult> {
    const startTime = Date.now();
    let entriesRemoved = 0;
    let spaceFreed = 0;
    let criticalEntriesPreserved = 0;

    console.info('Starting cache pruning operation');

    try {
      // Get current cache stats
      const cacheStats = this.fallbackService.getCacheStats();
      
      if (cacheStats.totalEntries <= this.config.maxCacheSize) {
        console.debug('Cache size within limits, skipping pruning');
        return {
          entriesRemoved: 0,
          spaceFreed: 0,
          criticalEntriesPreserved: 0,
          pruningDuration: Date.now() - startTime,
        };
      }

      // Prune by age first
      const ageBasedRemoved = this.fallbackService.pruneCacheByAge(this.config.maxCacheAge);
      entriesRemoved += ageBasedRemoved;

      // If still over limit, prune by usage
      const updatedStats = this.fallbackService.getCacheStats();
      if (updatedStats.totalEntries > this.config.maxCacheSize) {
        const usageBasedRemoved = this.fallbackService.pruneCacheByUsage(this.config.keepTopN);
        entriesRemoved += usageBasedRemoved;
      }

      // Prune React Query cache
      const reactQueryRemoved = await this.pruneReactQueryCache();
      entriesRemoved += reactQueryRemoved;

      // Calculate space freed (approximate)
      spaceFreed = entriesRemoved * 1024; // Rough estimate

      this.lastPruningTime = Date.now();

      console.info(`Cache pruning completed: ${entriesRemoved} entries removed`);

      return {
        entriesRemoved,
        spaceFreed,
        criticalEntriesPreserved,
        pruningDuration: Date.now() - startTime,
      };
    } catch (error) {
      console.error('Cache pruning failed:', error);
      return {
        entriesRemoved: 0,
        spaceFreed: 0,
        criticalEntriesPreserved: 0,
        pruningDuration: Date.now() - startTime,
      };
    }
  }

  public async emergencyPruning(): Promise<PruningResult> {
    console.warn('Performing emergency cache pruning');
    
    // More aggressive pruning settings
    const originalConfig = { ...this.config };
    this.config.maxCacheAge = 60 * 60 * 1000; // 1 hour
    this.config.keepTopN = 50; // Keep only top 50

    try {
      const result = await this.performPruning();
      
      // Force garbage collection if available
      if (typeof window !== 'undefined' && (window as any).gc) {
        (window as any).gc();
      }

      return result;
    } finally {
      // Restore original config
      this.config = originalConfig;
    }
  }

  public getCacheSizeInfo(): {
    totalEntries: number;
    maxEntries: number;
    utilizationPercentage: number;
    recommendsPruning: boolean;
    lastPruningTime: number;
  } {
    const stats = this.fallbackService.getCacheStats();
    const utilizationPercentage = (stats.totalEntries / this.config.maxCacheSize) * 100;

    return {
      totalEntries: stats.totalEntries,
      maxEntries: this.config.maxCacheSize,
      utilizationPercentage,
      recommendsPruning: utilizationPercentage > 80,
      lastPruningTime: this.lastPruningTime,
    };
  }

  public addCriticalQueryKey(queryKey: string): void {
    if (!this.config.criticalQueryKeys.includes(queryKey)) {
      this.config.criticalQueryKeys.push(queryKey);
    }
  }

  public removeCriticalQueryKey(queryKey: string): void {
    const index = this.config.criticalQueryKeys.indexOf(queryKey);
    if (index > -1) {
      this.config.criticalQueryKeys.splice(index, 1);
    }
  }

  private async pruneReactQueryCache(): Promise<number> {
    let removedCount = 0;
    const queryCache = this.queryClient.getQueryCache();
    const queries = queryCache.getAll();

    const cutoffTime = Date.now() - this.config.maxCacheAge;

    for (const query of queries) {
      const queryKey = query.queryKey.join(':');
      
      // Skip critical queries
      if (this.isCriticalQuery(queryKey)) {
        continue;
      }

      // Remove old queries
      if (query.state.dataUpdatedAt < cutoffTime) {
        this.queryClient.removeQueries({ queryKey: query.queryKey });
        removedCount++;
      }
    }

    return removedCount;
  }

  private isCriticalQuery(queryKey: string): boolean {
    return this.config.criticalQueryKeys.some(critical => 
      queryKey.includes(critical)
    );
  }

  public cleanup(): void {
    this.stopAutoPruning();
  }
}

// Global cache pruning service
let globalCachePruningService: CachePruningService | null = null;

export const initializeCachePruning = (
  queryClient: QueryClient,
  config?: Partial<CachePruningConfig>
): CachePruningService => {
  if (globalCachePruningService) {
    globalCachePruningService.cleanup();
  }

  globalCachePruningService = new CachePruningService(queryClient, config);
  return globalCachePruningService;
};

export const getCachePruningService = (): CachePruningService | null => {
  return globalCachePruningService;
};

// React hook for cache pruning
export const useCachePruningService = () => {
  return globalCachePruningService;
};